"use client";
import styles from "./HomeBanner.module.css";
import Image from "next/image";
import Head from "next/head";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNetworkStatus } from "react-adaptive-hooks";
import { useRouter } from "next/router";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import dynamic from "next/dynamic";
const CustomiseCta = dynamic(() => import("../CustomiseCta/CustomiseCta"));
const Slider = dynamic(() => import("react-slick"), { ssr: false });

import useIsMobile from "@/hooks/useIsMobile";

interface VideoLink {
  id: number;
  label: string;
  url: string;
  length: number;
}

interface Images {
  id: number;
  documentId: string;
  url: string;
  alternativeText: string;
}

interface BannerData {
  video_section: {
    title?: any;
    video_link: VideoLink | null;
    video_link_cta?: any[] | null;
    description?: any;
  };
  sliders: {
    id: number;
    desktop_img: Images;
    mobile_img: Images;
    ctas?: any[] | null;
    title: string;
    button_text: string;
  }[];
}

interface HeroBannerProps {
  data?: BannerData;
}

const HeroBanner = ({ data }: HeroBannerProps) => {
  const { effectiveConnectionType } = useNetworkStatus();
  const isMobile = useIsMobile(768);
  const [showVideo, setShowVideo] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const DEFAULT_DESKTOP_BANNER = "/assets/hero-video_frame.webp";
  const DEFAULT_MOBILE_BANNER = "/assets/home-def-mbl.webp";
  const DEFAULT_ALUMNI_DESKTOP_BANNER = "/assets/alumni-default-desktop.webp";
  const DEFAULT_ALUMNI_MOBILE_BANNER = "/assets/alumni-default-mobile.webp";

  const router = useRouter();
  const isAlumniPage = router?.pathname?.includes("/alumni");

  useEffect(() => {
    const shouldShowVideo = !isMobile || effectiveConnectionType === "4g";
    setShowVideo(shouldShowVideo);
  }, [effectiveConnectionType, isMobile]);

  const firstSliderImages = useMemo(() => {
    if (!data?.sliders?.length) {
      return {
        desktop: isAlumniPage
          ? DEFAULT_ALUMNI_DESKTOP_BANNER
          : DEFAULT_DESKTOP_BANNER,
        mobile: isAlumniPage
          ? DEFAULT_ALUMNI_MOBILE_BANNER
          : DEFAULT_MOBILE_BANNER,
      };
    }
    const desktopUrl = data?.sliders[0]?.desktop_img?.url?.replace(
      /\.(jpg|png)$/,
      ".webp"
    );
    const mobileUrl = data?.sliders[0]?.mobile_img?.url?.replace(
      /\.(jpg|png)$/,
      ".webp"
    );
    return {
      desktop: desktopUrl,
      mobile: mobileUrl,
    };
  }, [data, isAlumniPage]);

  const sliderSettings = useMemo(() => {
    const slidersCount = data?.sliders?.length || 0;
    return {
      dots: isMobile ? false : slidersCount > 1,
      infinite: slidersCount > 1,
      swipe: slidersCount > 1,
      speed: isMobile ? 300 : 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 4000,
      arrows: !isMobile && slidersCount > 1,
      pauseOnHover: true,
      pauseOnFocus: true,
      lazyLoad: "ondemand" as const,
      cssEase: "cubic-bezier(0.645, 0.045, 0.355, 1)",
      responsive: [
        {
          breakpoint: 760,
          settings: {
            arrows: false,
            dots: false,
            autoplaySpeed: 4000,
          },
        },
      ],
    };
  }, [data?.sliders?.length, isMobile]);

  const handleVideoError = useCallback(() => {
    setVideoError(true);
  }, []);

  const renderBannerContent = useCallback(() => {
    const hasVideoContent = data?.video_section?.video_link?.url;
    const hasSliderContent = data?.sliders && data.sliders.length > 0;

    const fallbackPoster = isMobile
      ? firstSliderImages?.mobile
      : firstSliderImages?.desktop;
    const shouldShowVideo = hasVideoContent && showVideo && !videoError;

    if (!shouldShowVideo && hasSliderContent) {
      return (
        <Slider {...sliderSettings} className={styles.swiperSlilk}>
          {data?.sliders?.map((slider, index) => (
            <div
              key={slider.id}
              className={styles.ImageAuto}
              style={{ cursor: "pointer" }}
            >
              <div className={styles.callout}>
                {/* <h1>{slider.title}</h1> */}
                <h1 dangerouslySetInnerHTML={{ __html: slider?.title }}></h1>
              </div>
              {slider?.ctas && Array.isArray(slider.ctas) && (
                <div className={styles.ButtonContainer}>
                  {slider?.ctas.map((cta, i) => (
                    <CustomiseCta key={cta.id || i} ctaData={cta} />
                  ))}
                </div>
              )}
              <Image
                src={
                  isMobile ? slider?.mobile_img?.url : slider?.desktop_img?.url
                }
                alt={
                  slider?.desktop_img?.alternativeText || "Hero Banner Image"
                }
                width={isMobile ? 800 : 1920}
                height={isMobile ? 400 : 880}
                sizes="(max-width: 760px) 100vw, (max-width: 1200px) 50vw, 1000px"
                style={{ width: "100%", height: "auto", objectFit: "cover" }}
                priority={index === 0}
                fetchPriority={index === 0 ? "high" : "auto"}
              />
            </div>
          ))}
        </Slider>
      );
    }

    return (
      <div className={styles.videoHomeContainer}>
        <div className={styles.overlay}></div>
        <div className={styles.callout}>
          {data?.video_section?.title && (
            <h1
              dangerouslySetInnerHTML={{ __html: data?.video_section?.title }}
            />
          )}
          {data?.video_section?.description && (
            <p className={styles.desc}>{data?.video_section?.description}</p>
          )}
        </div>
        {data?.video_section?.video_link_cta && (
          <div className={styles.ButtonContainer}>
            <CustomiseCta ctaData={data?.video_section?.video_link_cta} />
          </div>
        )}

        {/* Render both video and image to avoid layout shift */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          poster={fallbackPoster}
          onError={handleVideoError}
          className={styles.video}
          style={{ display: shouldShowVideo ? "block" : "none" }}
        >
          <source src={data?.video_section?.video_link?.url} type="video/mp4" />
        </video>

        <Image
          src={fallbackPoster}
          alt="Banner fallback"
          width={isMobile ? 800 : 1920}
          height={isMobile ? 400 : 880}
          priority
          fetchPriority="high"
          style={{
            width: "100%",
            height: "auto",
            objectFit: "cover",
            display: shouldShowVideo ? "none" : "block",
          }}
        />
      </div>
    );
  }, [
    data,
    isMobile,
    showVideo,
    videoError,
    firstSliderImages,
    sliderSettings,
    handleVideoError,
  ]);

  return (
    <section className={styles.homebannerheight}>
      <Head>
        <link
          rel="preload"
          as="image"
          href={firstSliderImages?.desktop}
          media="(min-width: 761px)"
          fetchPriority="high"
        />
        <link
          rel="preload"
          as="image"
          href={firstSliderImages?.mobile}
          media="(max-width: 760px)"
          fetchPriority="high"
        />
      </Head>
      <section className={styles.heroVideo} data-section="Home Banner">
        <div className={styles.videoContainer}>{renderBannerContent()}</div>
      </section>
    </section>
  );
};

export default HeroBanner;
